import { isResource } from '@medplum/core'
import type { Resource } from '@medplum/fhirtypes'
import { ExtendedResource } from '@seen/core/ExtendedResource'
import type { Query, QueryClient } from '@tanstack/react-query'
import { compact, isEqual } from 'lodash-es'
import {
  cacheKeys,
  readHistoryCacheKeyIdentifier,
  readVersionCacheKeyIdentifier,
} from '#src/hooks/cacheKeys.ts'

export function extractResourcesFromQuery(query: Query): Resource[] {
  const data = query.state.data
  const dataArray = Array.isArray(data) ? data : [data]
  return dataArray
    .filter((r) => isResource(r) || r instanceof ExtendedResource)
    .flatMap((item) => {
      if (item instanceof ExtendedResource) {
        return [item.toFhirResource()]
      }
      if (item.resourceType === 'Bundle') {
        return compact(item.entry?.map((e) => e.resource))
      }
      return [item]
    })
}

/* Deep-compare, NOT `meta.versionId` equality: response-local fields refresh
 * without bumping the version — most importantly signed Medplum Binary/attachment
 * URLs, which carry per-fetch signed params. A versionId-only guard would discard
 * a refetched resource that only changed its URL, pinning an expired URL in the
 * read cache and breaking downloads. Deep equality still collapses the identical
 * freshly-built objects that drive the write loop. */
export function isSameCachedResource(a: Resource, b: Resource): boolean {
  return isEqual(a, b)
}

/* Fan a query's resources out into individual `read` cache entries so
 * `useRead(type, id)` resolves from cache. Called from the QueryCache subscriber
 * on every `added`/`updated` event.
 *
 * `extractResourcesFromQuery` returns a freshly-built plain Resource on every
 * call (`ExtendedResource.toFhirResource()` / new object), so writing it back
 * unconditionally hands `setQueryData` a new reference even when nothing changed.
 * That fires another `updated` event, re-entering this subscriber — an unbounded
 * write loop that arms a fresh 5-minute gcTime timer per write and churns every
 * read-cache consumer's identity (e.g. the visit-note editor's `participant`,
 * forcing a TipTap reconfigure). Skip the write when the cached resource already
 * matches. */
export function cacheIndividualResources(
  query: Query,
  client: QueryClient
): void {
  const resources = extractResourcesFromQuery(query)
  resources.forEach((item) => {
    if (!item.id) {
      return
    }
    const key = cacheKeys.read(item.resourceType, item.id)
    if (
      isEqual(query.queryKey, key) ||
      // Historical reads must never overwrite the current/latest resource in
      // the read cache. readHistory fixed this bug once; readVersion is the
      // same hazard — version-pinned reads (completed-review baselines,
      // queued-edits drift-check draft bases) return OLD versions, and fanning
      // those out would clobber the live entries every current-state consumer
      // (labels, diffs, owner lookups) reads from.
      query.queryKey.includes(readHistoryCacheKeyIdentifier) ||
      query.queryKey.includes(readVersionCacheKeyIdentifier)
    ) {
      return
    }
    const existing = client.getQueryData<Resource>(key)
    if (existing && isSameCachedResource(existing, item)) {
      return
    }
    client.setQueryData(key, item)
  })
}
